import React, { useMemo, useRef } from 'react';
import EventStatus from '../event-teaser/EventStatus.jsx';
import SvgDefaultClock from '../svg/icons/default-clock.svg';
import SvgDefaultExternalLink from '../svg/icons/default-external-link.svg';

function MainEventContent({ time, location, duration, topline, title, subline, target }) {
  return (
    <>
      <div className="calendar-teaser__date">
        {time && <p className="text text--subline calendar-teaser__time">{time}</p>}

        <div className="calendar-teaser__info">
          {location && (
            <div className="calendar-teaser__location">
              <p className="text text--meta">{location.title}</p>
            </div>
          )}

          {duration && (
            <div className="calendar-teaser__duration">
              <SvgDefaultClock />

              <p className="text text--meta">{duration}</p>
            </div>
          )}
        </div>
      </div>

      <div className="calendar-teaser__content">
        <div data-stack="xs">
          {topline && <h3 className="text text--link">{topline}</h3>}

          <h2>
            <span className="text text--subline">{title}</span>

            {target && target === '_blank' && <SvgDefaultExternalLink />}
          </h2>

          {subline && <p className="text text--copy">{subline}</p>}
        </div>
      </div>
    </>
  );
}

function AdditionalEventContent({ time, title, info }) {
  return (
    <div className="calendar-teaser__additional-details">
      <div className="calendar-teaser__additional-content">
        {time && <p className="text text--date calendar-teaser__additional-time">{time}</p>}
        {title && <p className="text text--copy calendar-teaser__additional-title">{title}</p>}
      </div>
      {info && <div className="text text--meta text--italic calendar-teaser__additional-info" dangerouslySetInnerHTML={{ __html: info }} />}
    </div>
  );
}

function CalendarTeaserLink({ href, title, target, children }) {
  if (href) {
    return (
      <a className="calendar-teaser__link" href={href} title={title} target={target}>
        {children}
      </a>
    );
  }

  return <div className="calendar-teaser__link">{children}</div>;
}

export default function CalendarTeaser({
  theme = 'yellow',
  href,
  title,
  target,
  eventType = 'main',
  borderType = 'solid',
  features,
  topline,
  notExpired,
  status,
  statusValue,
  subline,
  ticket,
  time,
  info,
  location,
  duration,
  ticketUrl,
  image,
  video
}) {
  const getClasses = () => {
    const classes = [`calendar-teaser--event-${eventType}`, `calendar-teaser--border-${borderType}`];
    if (href) classes.push('calendar-teaser--has-link');
    if (topline) classes.push('calendar-teaser--has-topline');
    if (image && !video) classes.push('calendar-teaser--has-image');
    if (video) classes.push('calendar-teaser--has-video');
    if (features && features.length > 0) classes.push('calendar-teaser--has-features');

    return classes.join(' ');
  };

  const classes = useMemo(() => getClasses(), []);

  const ref = useRef(null);

  const playVideo = () => {
    if (ref.current) ref.current.play();
  };

  const pauseVideo = () => {
    if (ref.current) ref.current.pause();
  };

  return (
    <div className={`calendar-teaser ${classes}`} data-theme={theme} onMouseEnter={playVideo} onMouseLeave={pauseVideo}>
      <CalendarTeaserLink href={href} title={title} target={target}>
        {features && features.length > 0 && (
          <div className="tags tags--can-break calendar-teaser__features" data-theme="yellow">
            {features.map((i) => (
              <div key={i}>
                <span className="text text--hyphenate text--special ">{i}</span>
              </div>
            ))}
          </div>
        )}

        {eventType === 'main' && (
          <MainEventContent time={time} location={location} duration={duration} topline={topline} target={target} title={title} subline={subline} />
        )}
        {eventType === 'additional' && <AdditionalEventContent time={time} title={title} info={info} />}
      </CalendarTeaserLink>

      <div className="calendar-teaser__meta">
        {!notExpired && <EventStatus ticket={ticket} ticketUrl={ticketUrl} status={status} statusValue={statusValue} theme={theme} />}
      </div>

      <div className="calendar-teaser__image-wrapper">
        {image && !video && eventType !== 'additional' && (
          <figure className="calendar-teaser__image image">
            <div style={{ '--ratio': 1 }}>
              <img
                src={image.src}
                width={image.width}
                height={image.height}
                srcSet={image.srcset}
                loading="lazy"
                sizes="(min-width: 1440px) 33vw, (min-width: 768px) 50vw, 100vw"
                alt={image.title}
              />
            </div>
          </figure>
        )}

        {video && eventType !== 'additional' && (
          <figure className="calendar-teaser__image video-loop">
            <div style={{ '--ratio': 1 }}>
              <video ref={ref} src={video.src} muted loop playsInline />
            </div>
          </figure>
        )}
      </div>
    </div>
  );
}
